import { FunctionComponent } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { useLocation } from "wouter-preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $ossList, $ossListLoaded, type OSSInfo, updateOSSInfo } from "../../store/oss.ts";
import { $isLogined, $isSubAccount } from "../../store/user_info.ts";
import { gotoLoginPage, navigate } from "../../router_utils.ts";
import { useToast } from "../../components/toast/toast.tsx";
import { Icon } from "../../components/icon/icon.tsx";
import { PageHeader } from "../../components/ui/page_header.tsx";
import { DataGrid, DataGridColumn } from "../../components/ui/data_grid.tsx";

export const OSSListPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const isLogined = useStore($isLogined);
    const isSubAccount = useStore($isSubAccount);
    const ossList = useStore($ossList);
    const ossListLoaded = useStore($ossListLoaded);
    const [location, _setLocation] = useLocation();
    const toast = useToast();
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        document.title = t({ zh: "存储管理", en: "Storage Management" });
    }, [t]);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? "/oss");
        } else if (isSubAccount) {
            toast(t({ zh: "子账户无权访问此页面", en: "Access Denied" }), "error");
            navigate("/overview", { replace: true });
        }
    }, [isLogined, location, isSubAccount, toast, t]);

    useEffect(() => {
        if (!isLogined) return;
        if (!ossListLoaded) {
            updateOSSInfo().catch(() => {});
        }
    }, [isLogined, ossListLoaded]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await updateOSSInfo();
        } finally {
            setIsRefreshing(false);
        }
    };

    const formatDateYmd = (ms: number) => {
        const d = new Date(ms);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    };

    const columns: DataGridColumn<OSSInfo>[] = [
        {
            key: "id",
            title: "ID",
            width: "60px",
            align: "center"
        },
        {
            key: "oss_name",
            title: t({ zh: "名称", en: "Name" }),
            render: (oss) => <strong>{oss.oss_name}</strong>
        },
        {
            key: "oss_bucket",
            title: t({ zh: "Bucket", en: "Bucket" }),
            render: (oss) => (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Icon name="hard-drive" size={16} />
                    <span>{oss.oss_bucket}</span>
                </div>
            )
        },
        {
            key: "oss_endpoint",
            title: t({ zh: "Endpoint", en: "Endpoint" }),
            render: (oss) => <small>{oss.oss_endpoint}</small>
        },
        {
            key: "status",
            title: t({ zh: "状态", en: "Status" }),
            width: "120px",
            render: (oss) => {
                const isPaid = oss.paid;
                return (
                    <span class={`farpov_badge ${isPaid ? "success" : "neutral"}`}>
                        {isPaid ? t({ zh: "已启用", en: "Active" }) : t({ zh: "未启用", en: "Inactive" })}
                    </span>
                );
            }
        },
        {
            key: "actions",
            title: t({ zh: "操作", en: "Actions" }),
            align: "right",
            width: "100px",
            render: (oss) => (
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                    <button 
                        class="outline primary" 
                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem", width: "auto", marginBottom: 0 }}
                        onClick={() => navigate(`/oss/${oss.id}`, { replace: false })}
                        title={t({ zh: "编辑", en: "Edit" })}
                    >
                        <Icon name="edit" size={14} />
                    </button>
                </div>
            )
        }
    ];

    if (!isLogined) {
        return <article aria-busy="true" class="container"></article>;
    }

    return (
        <div class="farpov_top_container">
            <PageHeader 
                title={t({ zh: "存储管理", en: "Storage Management" })} 
                subtitle={t({ zh: "配置阿里云 OSS 存储桶信息", en: "Configure Aliyun OSS bucket information" })}
                actions={
                    <>
                        <button class="outline" disabled={isRefreshing} onClick={handleRefresh}>
                            <Icon name="refresh-cw" /> {t({ zh: "刷新", en: "Refresh" })}
                        </button>
                        <button onClick={() => navigate("/oss/new", { replace: false })}>
                            <Icon name="plus" /> {t({ zh: "新增存储", en: "New Storage" })}
                        </button>
                    </>
                }
            />
            
            <DataGrid 
                columns={columns} 
                data={ossList} 
                keyField="id"
                loading={!ossListLoaded && isRefreshing}
                emptyText={t({ zh: "暂无存储配置", en: "No storage configurations found" })}
                class="farpov_oss_grid"
            />
        </div>
    );
};

export default OSSListPage;
